import { createApi } from "@reduxjs/toolkit/query/react";
import * as config from "config";
import * as utils from "utils";

const baseQuery = async (args, api, extraOptions) => {
  try {
    const response = await utils.authenticatedFetch(
      config.endpoints.research(args.url),
      args.payload || {}
    );
    return {
      data: await response.json(),
    };
  } catch (error) {
    return { error };
  }
};

export const researchApi = createApi({
  reducerPath: "researchApi",
  baseQuery: baseQuery,
  tagTypes: ["ResearchSession"],
  endpoints: (builder) => ({
    getSession: builder.query({
      query: ({ submissionId }) => ({
        url: "get-session",
        payload: {
          body: JSON.stringify({ submissionId }),
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
        },
      }),
      providesTags: ["ResearchSession"],
    }),
    addItem: builder.mutation({
      query: ({
        submissionId,
        itemType,
        query: searchQuery,
        resultText,
        sources,
        note,
        provider,
        modelUsed,
        rawResponse,
        programData,
      }) => ({
        url: "add-item",
        payload: {
          body: JSON.stringify({
            submissionId,
            itemType,
            query: searchQuery,
            resultText,
            sources,
            note,
            provider,
            modelUsed,
            rawResponse,
            programData,
          }),
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
        },
      }),
      invalidatesTags: ["ResearchSession"],
    }),
    updateItem: builder.mutation({
      query: ({ submissionId, itemId, note, isCited, citedIn }) => ({
        url: "update-item",
        payload: {
          body: JSON.stringify({
            submissionId,
            itemId,
            note,
            isCited,
            citedIn,
          }),
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
        },
      }),
      invalidatesTags: ["ResearchSession"],
    }),
    citeSource: builder.mutation({
      query: ({ submissionId, itemId, sourceUrl, cited, citedIn }) => ({
        url: "cite-source",
        payload: {
          body: JSON.stringify({
            submissionId,
            itemId,
            sourceUrl,
            cited,
            citedIn,
          }),
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
        },
      }),
      invalidatesTags: ["ResearchSession"],
    }),
    deleteItem: builder.mutation({
      query: ({ submissionId, itemId }) => ({
        url: "delete-item",
        payload: {
          body: JSON.stringify({ submissionId, itemId }),
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
        },
      }),
      invalidatesTags: ["ResearchSession"],
    }),
    addReferenceFiling: builder.mutation({
      query: ({ submissionId, identifier, formTypes }) => ({
        url: "add-reference-filing",
        payload: {
          body: JSON.stringify({ submissionId, identifier, formTypes }),
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
        },
      }),
      invalidatesTags: ["ResearchSession"],
    }),
    saveFilingAttachment: builder.mutation({
      query: ({ submissionId, itemId }) => ({
        url: "save-filing-attachment",
        payload: {
          body: JSON.stringify({ submissionId, itemId }),
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
        },
      }),
      invalidatesTags: ["ResearchSession"],
    }),
    aiFollowup: builder.mutation({
      query: ({ submissionId, itemId, query, programData }) => ({
        url: "ai-followup",
        payload: {
          body: JSON.stringify({
            submissionId,
            itemId,
            query,
            programData,
          }),
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
        },
      }),
      invalidatesTags: ["ResearchSession"],
    }),
  }),
});

export const {
  useGetSessionQuery,
  useAddItemMutation,
  useUpdateItemMutation,
  useCiteSourceMutation,
  useDeleteItemMutation,
  useAddReferenceFilingMutation,
  useSaveFilingAttachmentMutation,
  useAiFollowupMutation,
} = researchApi;
